/* ============================================================
   HARRIS CAR CARE — Theme interactions
   Ported 1:1 from the approved design source of truth.
   Wrapped in scoped initialisers so every interaction also
   re-binds correctly inside the Shopify Theme Editor
   (shopify:section:load / :select / :unload).
   ============================================================ */
(function () {
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Module-scoped refs updated on each init so global scroll
     handlers survive section re-renders in the editor. */
  var io = null;
  var headerEl = null;
  var hbarEl = null;
  var heroMedia = null;
  var globalBound = false;
  var lastY = 0;
  var scrollTicking = false;
  var topbarTicking = false;
  /* Set by initMobileNav so the Escape key reuses its close routine. */
  var closeMobileNav = null;

  function revealObserver() {
    if (io) return io;
    io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); }
      });
    }, { threshold: 0.14, rootMargin: '0px 0px -8% 0px' });
    return io;
  }

  function observeReveals(scope) {
    var obs = revealObserver();
    (scope || document).querySelectorAll('.reveal, .img-reveal').forEach(function (el) {
      if (!el.dataset.obs) { el.dataset.obs = '1'; obs.observe(el); }
    });
  }

  /* ---- Sticky header + scroll progress + parallax (bound once) ----
     Scroll fires far more often than the screen refreshes, and any layout read
     that follows a style write forces a synchronous recalc. So the work is
     coalesced into one requestAnimationFrame per frame (the same pattern as
     onScrollParallax below), and inside that frame every measurement is taken
     up front, before a single class or style is written. */
  function updateScroll() {
    scrollTicking = false;

    /* --- reads: take every measurement before touching the DOM --- */
    var y = window.scrollY;
    var de = document.documentElement;
    var scrollTop = de.scrollTop;
    var scrollRange = de.scrollHeight - de.clientHeight;

    /* --- writes: dataset lookups are attribute reads, not layout reads --- */
    if (headerEl) {
      var threshold = parseInt(headerEl.dataset.threshold, 10);
      if (isNaN(threshold)) { threshold = 40; }
      var transparent = headerEl.dataset.transparent !== 'false';
      var sticky = headerEl.dataset.sticky !== 'false';
      var mode = headerEl.dataset.mode || 'hide-show';
      if (transparent) {
        headerEl.classList.toggle('scrolled', y > threshold);
      } else {
        headerEl.classList.add('scrolled');
      }
      if (sticky && mode === 'hide-show' && y > 600 && y > lastY) {
        headerEl.classList.add('hide');
      } else {
        headerEl.classList.remove('hide');
      }
    }
    if (hbarEl) {
      var p = scrollTop / Math.max(1, scrollRange);
      hbarEl.style.width = (Math.min(1, p) * 100) + '%';
    }
    lastY = y;
  }

  function onScroll() {
    if (scrollTicking) return;
    scrollTicking = true;
    requestAnimationFrame(updateScroll);
  }

  var parallaxTicking = false;
  function onScrollParallax() {
    if (parallaxTicking) return; parallaxTicking = true;
    requestAnimationFrame(function () {
      var y = window.scrollY;
      if (heroMedia && y < window.innerHeight) { heroMedia.style.transform = 'translateY(' + (y * 0.22) + 'px)'; }
      parallaxTicking = false;
    });
  }

  function bindGlobal() {
    if (globalBound) return; globalBound = true;
    window.addEventListener('scroll', onScroll, { passive: true });
    /* Init-time calls stay synchronous so the header state and progress bar are
       correct on first paint rather than one frame late. */
    updateScroll();
    /* Rotating to landscape, or resizing, changes whether the announcement text
       wraps — so the header's offset has to be re-measured, not assumed. */
    window.addEventListener('resize', function () {
      if (topbarTicking) return;
      topbarTicking = true;
      requestAnimationFrame(function () { topbarTicking = false; syncTopbarOffset(); });
    }, { passive: true });
    if (!reduce) window.addEventListener('scroll', onScrollParallax, { passive: true });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        var m = document.getElementById('mnav');
        if (m && m.classList.contains('open') && closeMobileNav) { closeMobileNav(); }
      }
    });
  }

  /* ---- Announcement-bar height → --topbar-h ----
     The un-scrolled header is fixed at `top: var(--topbar-h, 40px)`, sized to
     sit directly under the announcement bar. 40px is the bar's height for one
     line of 11px text — but on a narrow phone a longer announcement wraps to
     two lines and the bar grows to ~57px, which used to leave the header
     covering the bar's second line. Measuring it keeps the two in step at every
     width and for any announcement length. Removing the bar (or turning it off)
     leaves no element to measure, so the offset correctly falls to 0. */
  function syncTopbarOffset() {
    var bar = document.querySelector('.topbar');
    var h = bar ? Math.round(bar.getBoundingClientRect().height) : 0;
    document.documentElement.style.setProperty('--topbar-h', h + 'px');
  }

  /* ---- Header (refs refreshed each init) ---- */
  function initHeader(scope) {
    var h = (scope && scope.querySelector && scope.querySelector('#header')) || document.getElementById('header');
    if (h) headerEl = h;
    var b = document.getElementById('hprogbar');
    if (b) hbarEl = b;
    syncTopbarOffset();
    updateScroll();
  }

  /* ---- Hero intro + video fallback + parallax target ---- */
  function initHero(scope) {
    var hero = (scope && scope.querySelector && scope.querySelector('.hero')) || document.querySelector('.hero');
    if (!hero || hero.dataset.ready) { if (hero) heroMedia = hero.querySelector('.hero__media'); return; }
    hero.dataset.ready = '1';
    heroMedia = hero.querySelector('.hero__media');
    requestAnimationFrame(function () { setTimeout(function () { hero.classList.add('in'); }, 120); });

    var hv = hero.querySelector('.hero__media video');
    var hi = hero.querySelector('.hero__media .kb');
    if (hv) {
      hv.addEventListener('error', function () { hv.style.display = 'none'; if (hi) hi.style.display = 'block'; });
      setTimeout(function () { if (hv.readyState < 2 && hi) { hi.style.display = 'block'; } }, 2600);
    }
  }

  /* ---- Magnetic buttons ---- */
  function initMagnetic(scope) {
    if (reduce || !window.matchMedia('(pointer:fine)').matches) return;
    (scope || document).querySelectorAll('.btn, .snav').forEach(function (btn) {
      if (btn.dataset.mag) return; btn.dataset.mag = '1';
      /* mousemove fires far more often than the screen refreshes, and each pass
         measured the button (a forced layout, because the previous pass had just
         written its transform) and wrote it again. The pointer position is now
         only recorded on the event; the measure-and-write runs at most once per
         frame, which is the fastest the effect could ever be seen anyway. */
      var magRaf = null, magX = 0, magY = 0;
      var applyMagnet = function () {
        magRaf = null;
        var r = btn.getBoundingClientRect();
        var mx = magX - r.left - r.width / 2;
        var my = magY - r.top - r.height / 2;
        btn.style.transform = 'translate(' + (mx * 0.25) + 'px,' + (my * 0.35) + 'px)';
      };
      btn.addEventListener('mousemove', function (e) {
        magX = e.clientX; magY = e.clientY;
        if (!magRaf) { magRaf = requestAnimationFrame(applyMagnet); }
      });
      btn.addEventListener('mouseleave', function () {
        /* Drop any frame still queued, so it cannot re-apply the offset after
           the reset and leave the button stuck off-centre. */
        if (magRaf) { cancelAnimationFrame(magRaf); magRaf = null; }
        btn.style.transform = '';
      });
    });
  }

  /* ---- Product card hover image (loaded on demand) ----
     The second image is only ever revealed by :hover, so it is rendered without
     a src and promoted here. Gated on a fine pointer, so touch devices — where
     the hover state is unreachable — never request it at all, and bound per card
     so even on desktop only the cards actually hovered fetch anything. */
  function initPcardHover(scope) {
    if (!window.matchMedia('(pointer:fine)').matches) return;
    var root = scope && scope.querySelectorAll ? scope : document;
    root.querySelectorAll('[data-pcard-secondary]').forEach(function (img) {
      if (img.dataset.hoverBound) return;
      img.dataset.hoverBound = '1';
      var card = img.closest('.pcard');
      if (!card) return;
      var load = function () {
        card.removeEventListener('mouseenter', load);
        card.removeEventListener('focusin', load);
        var ss = img.getAttribute('data-srcset');
        if (ss) { img.setAttribute('srcset', ss); }
        img.setAttribute('src', img.getAttribute('data-src') || '');
      };
      card.addEventListener('mouseenter', load);
      /* Keyboard users reach the card without ever generating a mouseenter. */
      card.addEventListener('focusin', load);
    });
  }

  /* ---- Product slider: drag + arrows ---- */
  function initSlider(scope) {
    var root = scope && scope.querySelector ? scope : document;
    var track = root.querySelector('#track') || document.getElementById('track');
    if (!track || track.dataset.ready) return;
    track.dataset.ready = '1';
    var slider = track.closest('.slider') || document;
    var prev = slider.querySelector('#prev');
    var next = slider.querySelector('#next');

    var step = function () { var card = track.querySelector('.pcard'); return card ? card.offsetWidth + 22 : 320; };
    var clampScroll = function () {
      var max = track.scrollWidth - track.clientWidth;
      if (prev) prev.disabled = track.scrollLeft < 4;
      if (next) next.disabled = track.scrollLeft > max - 4;
    };
    if (next) next.addEventListener('click', function () { track.scrollBy({ left: step() * 1.4, behavior: 'smooth' }); });
    if (prev) prev.addEventListener('click', function () { track.scrollBy({ left: -step() * 1.4, behavior: 'smooth' }); });
    track.addEventListener('scroll', clampScroll, { passive: true });
    clampScroll();

    var down = false, dragging = false, startX = 0, startS = 0, moved = 0, pid = null;
    var DRAG_THRESHOLD = 6;
    /* Writing scrollLeft forces layout, and pointermove outruns the refresh rate,
       so the write is coalesced to one frame holding the newest position. The
       threshold test and pointer capture stay on the event itself — capture has
       to happen inside the handler to be honoured. */
    var dragRaf = null, dragTarget = 0;
    var applyDrag = function () { dragRaf = null; track.scrollLeft = dragTarget; };
    track.addEventListener('pointerdown', function (e) {
      down = true; dragging = false; moved = 0; pid = e.pointerId;
      startX = e.clientX; startS = track.scrollLeft;
    });
    track.addEventListener('pointermove', function (e) {
      if (!down) return;
      var dx = e.clientX - startX; moved = Math.abs(dx);
      /* Only begin dragging — and capture the pointer — once past the threshold.
         A genuine click/tap never captures the pointer, so the card's link
         activates normally; this is what makes whole-card navigation reliable
         inside the slider while keeping drag/swipe intact. */
      if (!dragging && moved > DRAG_THRESHOLD) {
        dragging = true;
        track.classList.add('drag');
        try { track.setPointerCapture(pid); } catch (err) {}
      }
      if (dragging) {
        dragTarget = startS - dx;
        if (!dragRaf) { dragRaf = requestAnimationFrame(applyDrag); }
      }
    });
    var endDrag = function () { down = false; track.classList.remove('drag'); };
    track.addEventListener('pointerup', endDrag);
    track.addEventListener('pointercancel', endDrag);
    /* Suppress only the click that immediately follows a real drag, so dragging
       never opens a product. Delegated + capture phase so it reliably covers the
       whole-card stretched link and any interactive controls; genuine clicks pass. */
    track.addEventListener('click', function (e) {
      if (dragging) { e.preventDefault(); e.stopPropagation(); dragging = false; }
    }, true);
  }

  /* ---- Before / After slider ---- */
  function initBA(scope) {
    var root = scope && scope.querySelector ? scope : document;
    var ba = root.querySelector('#ba') || document.getElementById('ba');
    if (!ba || ba.dataset.ready) return;
    ba.dataset.ready = '1';
    var after = ba.querySelector('#baAfter') || ba.querySelector('.ba__after');
    var handle = ba.querySelector('#baHandle') || ba.querySelector('.ba__handle');
    if (!after || !handle) return;
    var active = false;
    /* pointermove can fire several times per screen refresh (240Hz on some
       touch hardware). Each pass measured the element, then wrote clip-path on a
       full-width image and a layout-triggering `left` — so the measure-and-write
       is coalesced to one frame, keeping only the newest pointer position. */
    var posRaf = null, posX = 0;
    var applyPos = function () {
      posRaf = null;
      var r = ba.getBoundingClientRect();
      if (!r.width) return;
      var p = (posX - r.left) / r.width;
      p = Math.max(0, Math.min(1, p));
      after.style.clipPath = 'inset(0 0 0 ' + (p * 100) + '%)';
      handle.style.left = (p * 100) + '%';
    };
    var setPos = function (clientX) {
      posX = clientX;
      if (!posRaf) { posRaf = requestAnimationFrame(applyPos); }
    };
    ba.addEventListener('pointerdown', function (e) { active = true; setPos(e.clientX); ba.setPointerCapture(e.pointerId); });
    ba.addEventListener('pointermove', function (e) { if (active) setPos(e.clientX); });
    var stop = function () { active = false; };
    ba.addEventListener('pointerup', stop); ba.addEventListener('pointercancel', stop);
    if (!reduce) {
      var t = 0;
      var hint = function () {
        t += 0.02; var p = 0.5 + Math.sin(t) * 0.04;
        after.style.clipPath = 'inset(0 0 0 ' + (p * 100) + '%)'; handle.style.left = (p * 100) + '%';
        if (!active && t < Math.PI * 2) requestAnimationFrame(hint);
      };
      var hio = new IntersectionObserver(function (es) {
        es.forEach(function (en) { if (en.isIntersecting) { requestAnimationFrame(hint); hio.disconnect(); } });
      }, { threshold: 0.4 });
      hio.observe(ba);
    }
  }

  /* ---- Video feature play toggle ---- */
  function initVideo(scope) {
    var root = scope && scope.querySelector ? scope : document;
    var fvid = root.querySelector('#fvid') || document.getElementById('fvid');
    var playbtn = root.querySelector('#playbtn') || document.getElementById('playbtn');
    if (!fvid || !playbtn || playbtn.dataset.ready) return;
    playbtn.dataset.ready = '1';
    var fposter = root.querySelector('#fposter') || document.getElementById('fposter');
    playbtn.addEventListener('click', function () {
      if (fvid.paused) {
        fvid.play().then(function () { playbtn.classList.add('playing'); if (fposter) fposter.style.opacity = '0'; }).catch(function () {});
      } else { fvid.pause(); playbtn.classList.remove('playing'); }
    });
    fvid.addEventListener('error', function () {});
  }

  /* ---- Testimonials carousel (multi-card, scroll-snap track) ---- */
  function initTesti(scope) {
    var root = scope && scope.querySelector ? scope : document;
    var viewport = root.querySelector('#testi') || document.getElementById('testi');
    if (!viewport || viewport.dataset.ready) return;
    viewport.dataset.ready = '1';
    var container = viewport.closest('.testi') || document;
    var track = viewport.querySelector('.testi__track');
    var cards = track ? Array.prototype.slice.call(track.querySelectorAll('.testi-card')) : [];
    var dotsWrap = container.querySelector('.testi__dots');
    var prev = container.querySelector('.testi__nav--prev');
    var next = container.querySelector('.testi__nav--next');
    if (!cards.length) return;

    var current = 0, timer = null;

    // How many cards fit in the viewport right now (1 mobile / 2 / 3 desktop).
    var perView = function () {
      var cw = cards[0].getBoundingClientRect().width;
      if (!cw) return 1;
      return Math.max(1, Math.round(viewport.clientWidth / cw));
    };
    var pageCount = function () { return Math.max(1, Math.ceil(cards.length / perView())); };
    // scrollLeft offset that puts a given card's left edge at the viewport start.
    var cardOffset = function (idx) { return cards[idx].offsetLeft - cards[0].offsetLeft; };

    var setActive = function (p) {
      current = p;
      if (!dotsWrap) return;
      var ds = dotsWrap.querySelectorAll('.tdot');
      for (var k = 0; k < ds.length; k++) {
        var on = k === p;
        ds[k].classList.toggle('on', on);
        ds[k].setAttribute('aria-selected', on ? 'true' : 'false');
      }
    };

    var buildDots = function () {
      if (!dotsWrap) return;
      dotsWrap.innerHTML = '';
      var pc = pageCount();
      for (var p = 0; p < pc; p++) {
        (function (p) {
          var b = document.createElement('button');
          b.className = 'tdot';
          b.type = 'button';
          b.setAttribute('role', 'tab');
          b.setAttribute('aria-label', 'Slide ' + (p + 1) + ' of ' + pc);
          b.addEventListener('click', function () { goTo(p, true); });
          dotsWrap.appendChild(b);
        })(p);
      }
    };

    var goTo = function (p, user) {
      var pc = pageCount();
      p = (p + pc) % pc;
      var idx = Math.min(p * perView(), cards.length - 1);
      viewport.scrollTo({ left: cardOffset(idx), behavior: reduce ? 'auto' : 'smooth' });
      setActive(p);
      if (user) resetAuto();
    };

    // Keep dots in sync when the user swipes/scrolls the track directly.
    var raf = null;
    viewport.addEventListener('scroll', function () {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(function () {
        var sl = viewport.scrollLeft, nearest = 0, best = Infinity;
        for (var n = 0; n < cards.length; n++) {
          var d = Math.abs(cardOffset(n) - sl);
          if (d < best) { best = d; nearest = n; }
        }
        var p = Math.min(Math.floor(nearest / perView()), pageCount() - 1);
        if (p !== current) setActive(p);
      });
    }, { passive: true });

    var canAuto = function () { return !reduce && pageCount() > 1; };
    var auto = function () { if (!canAuto()) return; stopAuto(); timer = setInterval(function () { goTo(current + 1, false); }, 6000); };
    var stopAuto = function () { if (timer) { clearInterval(timer); timer = null; } };
    var resetAuto = function () { stopAuto(); auto(); };

    if (prev) prev.addEventListener('click', function () { goTo(current - 1, true); });
    if (next) next.addEventListener('click', function () { goTo(current + 1, true); });
    container.addEventListener('mouseenter', stopAuto);
    container.addEventListener('mouseleave', auto);
    container.addEventListener('focusin', stopAuto);

    var rebuild = function () {
      var p = Math.min(current, pageCount() - 1);
      buildDots(); setActive(p);
      viewport.scrollTo({ left: cardOffset(Math.min(p * perView(), cards.length - 1)), behavior: 'auto' });
    };
    var rzTimer = null;
    window.addEventListener('resize', function () {
      clearTimeout(rzTimer);
      rzTimer = setTimeout(rebuild, 180);
    });

    buildDots(); setActive(0); auto();
  }

  /* ---- Newsletter (progressive enhancement; Shopify form still submits) ---- */
  function initNewsletter(scope) {
    var root = scope && scope.querySelector ? scope : document;
    var form = root.querySelector('#newsForm') || document.getElementById('newsForm');
    if (!form || form.dataset.ready) return;
    form.dataset.ready = '1';
    var ok = (form.parentNode && form.parentNode.querySelector('#newsOk')) || document.getElementById('newsOk');
    form.addEventListener('submit', function () { if (ok) ok.classList.add('on'); });
  }

  /* ---- Mobile menu ---- */
  function initMobileNav(scope) {
    var burger = document.querySelector('.burger');
    var mnav = document.getElementById('mnav');
    var mnavClose = document.getElementById('mnavClose');
    var backdrop = document.getElementById('mnavBackdrop');
    if (!burger || !mnav || burger.dataset.ready) return;
    burger.dataset.ready = '1';
    var openM = function () {
      mnav.classList.add('open'); mnav.setAttribute('aria-hidden', 'false');
      if (backdrop) backdrop.classList.add('open');
      burger.setAttribute('aria-expanded', 'true'); document.body.style.overflow = 'hidden';
      if (mnavClose) mnavClose.focus();
    };
    var closeM = function () {
      mnav.classList.remove('open'); mnav.setAttribute('aria-hidden', 'true');
      if (backdrop) backdrop.classList.remove('open');
      burger.setAttribute('aria-expanded', 'false'); document.body.style.overflow = '';
      burger.focus();
    };
    burger.addEventListener('click', openM);
    if (mnavClose) mnavClose.addEventListener('click', closeM);
    if (backdrop) backdrop.addEventListener('click', closeM);
    mnav.querySelectorAll('a').forEach(function (a) { a.addEventListener('click', closeM); });
    /* Published so the global Escape handler closes via this same function
       instead of keeping its own copy of the teardown steps. */
    closeMobileNav = closeM;
  }

  /* ---- Add to cart (AJAX add for every product form) ----
     Covers the product-card quick-add AND the product page form: any form that
     posts to /cart/add is intercepted, so nothing falls back to a full page
     reload into /cart. */
  function cartRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  /* The drawer is rendered statically from layout/theme.liquid, so its Section
     Rendering API id is read straight off the element — the request and the
     drawer can never disagree about which section to render. */
  function drawerSectionId() {
    var d = document.getElementById('CartDrawer');
    return d ? d.getAttribute('data-section-id') : null;
  }

  function setCartCount(n) {
    if (n === null || n === undefined || n === '') return;
    document.querySelectorAll('.icons .cart .count, [data-cart-count-text]').forEach(function (el) {
      el.textContent = n;
    });
  }

  function refreshCartCount() {
    fetch(cartRoot() + 'cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (c) { setCartCount(c.item_count); })
      .catch(function () {});
  }

  /* Asks for the drawer markup inside the same POST, so the rendered line item
     comes back with the add response instead of a round trip later. That is what
     makes the item appear the instant the drawer opens. */
  function postAdd(form) {
    var body = new FormData(form);
    var sid = drawerSectionId();
    if (sid) {
      body.append('sections', sid);
      body.append('sections_url', window.location.pathname + window.location.search);
    }
    return fetch(cartRoot() + 'cart/add.js', {
      method: 'POST',
      body: body,
      headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
    }).then(function (r) {
      return r.json().then(function (data) {
        /* Shopify answers 422 for sold out / stock limits with a usable message;
           without this check a failed add still reported success. */
        if (!r.ok) {
          var err = new Error(data.description || data.message || 'Could not add to cart');
          err.data = data;
          throw err;
        }
        return data;
      });
    });
  }

  function atcError(form, message) {
    var el = form.querySelector('[data-atc-error]');
    if (!message) { if (el) el.remove(); return; }
    if (!el) {
      el = document.createElement('p');
      el.className = 'atc-error';
      el.setAttribute('data-atc-error', '');
      el.setAttribute('role', 'alert');
      form.appendChild(el);
    }
    el.textContent = message;
  }

  function bindAddForm(form) {
    if (!form || form.dataset.cartReady) return;
    form.dataset.cartReady = '1';
    form.addEventListener('submit', function (e) {
      var btn = form.querySelector('[type="submit"]');
      if (btn && btn.disabled) { e.preventDefault(); return; }
      e.preventDefault();
      /* The product page wraps its label in a span; the card button is its own label. */
      var labelEl = (btn && btn.querySelector('[data-pdp-atc-label]')) || btn;
      var original = labelEl ? labelEl.textContent : '';
      atcError(form, '');
      if (btn) { btn.disabled = true; btn.setAttribute('aria-busy', 'true'); }
      if (labelEl) labelEl.textContent = 'Adding…';

      var restore = function (text) {
        /* aria-disabled is the source of truth for availability — the variant
           picker may have switched to a sold-out variant while this was in flight. */
        if (btn) {
          btn.disabled = btn.getAttribute('aria-disabled') === 'true';
          btn.removeAttribute('aria-busy');
        }
        if (!labelEl) return;
        labelEl.textContent = text;
        setTimeout(function () { labelEl.textContent = original; }, 1400);
      };

      postAdd(form)
        .then(function (data) {
          var sid = drawerSectionId();
          var sections = data.sections || {};
          /* With the drawer present its markup carries the new count, so no
             extra /cart.js call is needed. */
          if (sid && sections[sid]) {
            document.dispatchEvent(new CustomEvent('cart:added', { detail: { item: data, sections: sections } }));
          } else {
            refreshCartCount();
            document.dispatchEvent(new CustomEvent('cart:added', { detail: { item: data, sections: null } }));
          }
          restore('Added ✓');
        })
        .catch(function (err) {
          atcError(form, err && err.message ? err.message : 'Could not add to cart');
          restore(original);
        });
    });
  }

  function initCart(scope) {
    var root = (scope && scope.querySelectorAll) ? scope : document;
    root.querySelectorAll('form[action*="/cart/add"]').forEach(bindAddForm);
  }

  /* ---- Announcement bar rotation (only when >1 bar + auto-rotate) ---- */
  function initAnnouncement(scope) {
    var root = scope && scope.querySelectorAll ? scope : document;
    root.querySelectorAll('.topbar[data-rotate]').forEach(function (bar) {
      if (bar.dataset.rot) return; bar.dataset.rot = '1';
      var ticks = bar.querySelectorAll('.tick');
      if (ticks.length < 2) return;
      var speed = parseInt(bar.dataset.speed, 10) || 5000;
      var i = 0, timer = null;
      var show = function (n) {
        ticks[i].classList.remove('is-active'); ticks[i].setAttribute('aria-hidden', 'true');
        i = (n + ticks.length) % ticks.length;
        ticks[i].classList.add('is-active'); ticks[i].removeAttribute('aria-hidden');
      };
      var play = function () { if (!timer) timer = setInterval(function () { show(i + 1); }, speed); };
      var stop = function () { if (timer) { clearInterval(timer); timer = null; } };
      /* Pausable per WCAG 2.2.2 (pause on hover / keyboard focus). */
      bar.addEventListener('mouseenter', stop);
      bar.addEventListener('mouseleave', play);
      bar.addEventListener('focusin', stop);
      bar.addEventListener('focusout', play);
      play();
    });
  }

  /* ---- Mega menu (desktop): hover/focus open, close-intent delay, no flicker ---- */
  function initMegaMenu(scope) {
    var header = (scope && scope.querySelector && scope.querySelector('#header')) || document.getElementById('header');
    if (!header || header.dataset.megaReady) return;
    var triggers = header.querySelectorAll('[data-mega-target]');
    if (!triggers.length) return;
    header.dataset.megaReady = '1';
    var closeTimer = null, openTimer = null;
    var closeAll = function () {
      header.querySelectorAll('.mega.is-open').forEach(function (p) { p.classList.remove('is-open'); });
      triggers.forEach(function (t) { t.setAttribute('aria-expanded', 'false'); });
      header.classList.remove('has-mega');
    };
    var openPanel = function (trigger, panel) {
      clearTimeout(closeTimer);
      header.querySelectorAll('.mega.is-open').forEach(function (p) { if (p !== panel) { p.classList.remove('is-open'); } });
      panel.classList.add('is-open');
      trigger.setAttribute('aria-expanded', 'true');
      header.classList.add('has-mega');
      header.style.setProperty('--mega-overlay-active', panel.getAttribute('data-overlay') || 'transparent');
    };
    triggers.forEach(function (trigger) {
      var panel = document.getElementById(trigger.getAttribute('data-mega-target'));
      if (!panel) return;
      var mode = trigger.getAttribute('data-open') || 'hover';
      var od = parseInt(trigger.getAttribute('data-open-delay'), 10); if (isNaN(od)) { od = 0; }
      var cd = parseInt(trigger.getAttribute('data-close-delay'), 10); if (isNaN(cd)) { cd = 140; }
      if (mode === 'click') {
        trigger.addEventListener('click', function (e) {
          e.preventDefault();
          if (panel.classList.contains('is-open')) { closeAll(); } else { openPanel(trigger, panel); }
        });
      } else {
        trigger.addEventListener('mouseenter', function () { clearTimeout(closeTimer); openTimer = setTimeout(function () { openPanel(trigger, panel); }, od); });
        trigger.addEventListener('mouseleave', function () { clearTimeout(openTimer); closeTimer = setTimeout(closeAll, cd); });
        trigger.addEventListener('focus', function () { openPanel(trigger, panel); });
        panel.addEventListener('mouseenter', function () { clearTimeout(closeTimer); });
        panel.addEventListener('mouseleave', function () { closeTimer = setTimeout(closeAll, cd); });
      }
    });
    header.addEventListener('mouseleave', function () { clearTimeout(openTimer); closeTimer = setTimeout(closeAll, 140); });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape') { closeAll(); } });
    document.addEventListener('click', function (e) {
      if (header.classList.contains('has-mega') && !header.contains(e.target)) { closeAll(); }
    });
  }

  /* ---- Product page: gallery crossfade, swatches, qty, accordions ---- */
  function initPdp(scope) {
    var root = scope && scope.querySelector ? scope : document;
    var pdp = root.querySelector('[data-pdp]') || document.querySelector('[data-pdp]');
    if (!pdp || pdp.dataset.ready) return;
    pdp.dataset.ready = '1';

    /* -- Gallery: thumbnails swap the main image via an opacity crossfade -- */
    var slides = Array.prototype.slice.call(pdp.querySelectorAll('[data-pdp-slide]'));
    var thumbs = Array.prototype.slice.call(pdp.querySelectorAll('[data-pdp-thumb]'));

    var showMedia = function (mediaId) {
      if (!mediaId) return;
      var found = false;
      slides.forEach(function (sl) {
        var on = sl.getAttribute('data-media-id') === String(mediaId);
        if (on) found = true;
        sl.classList.toggle('is-active', on);
        if (on) { sl.removeAttribute('aria-hidden'); } else { sl.setAttribute('aria-hidden', 'true'); }
      });
      if (!found) return;
      thumbs.forEach(function (t) {
        var on = t.getAttribute('data-media-id') === String(mediaId);
        t.classList.toggle('is-active', on);
        t.setAttribute('aria-selected', on ? 'true' : 'false');
      });
    };

    thumbs.forEach(function (t) {
      t.addEventListener('click', function () { showMedia(t.getAttribute('data-media-id')); });
    });

    /* Arrow-key navigation across the thumbnail rail (it is a tablist). */
    pdp.addEventListener('keydown', function (e) {
      if (!thumbs.length) return;
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp' && e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      var i = thumbs.indexOf(document.activeElement);
      if (i === -1) return;
      e.preventDefault();
      var fwd = e.key === 'ArrowDown' || e.key === 'ArrowRight';
      var n = (i + (fwd ? 1 : -1) + thumbs.length) % thumbs.length;
      thumbs[n].focus();
      showMedia(thumbs[n].getAttribute('data-media-id'));
    });

    /* -- Variants: swatch click updates id, price, availability and image -- */
    var variants = [];
    var vJson = pdp.querySelector('[data-pdp-variants]');
    if (vJson) { try { variants = JSON.parse(vJson.textContent); } catch (err) { variants = []; } }

    var vInput = pdp.querySelector('[data-pdp-variant-input]');
    var amountEl = pdp.querySelector('[data-pdp-amount]');
    var priceWrap = pdp.querySelector('[data-pdp-price]');
    var vTitleEl = pdp.querySelector('[data-pdp-variant-title]');
    var atc = pdp.querySelector('[data-pdp-atc]');
    var atcLabel = pdp.querySelector('[data-pdp-atc-label]');
    var soldOutText = atc ? atc.getAttribute('data-sold-out') : null;

    var findVariant = function (id) {
      for (var i = 0; i < variants.length; i++) {
        if (String(variants[i].id) === String(id)) return variants[i];
      }
      return null;
    };

    var selectVariant = function (id) {
      var v = findVariant(id);
      if (!v) return;
      if (vInput) vInput.value = v.id;
      if (amountEl) amountEl.textContent = v.price;

      /* Compare-at is per-variant, so the <s> is created/removed on the fly. */
      if (priceWrap) {
        var cmp = priceWrap.querySelector('[data-pdp-compare]');
        if (v.compare_at_price) {
          if (!cmp) {
            cmp = document.createElement('s');
            cmp.setAttribute('data-pdp-compare', '');
            priceWrap.insertBefore(cmp, priceWrap.firstChild);
          }
          cmp.textContent = v.compare_at_price;
        } else if (cmp) {
          cmp.remove();
        }
      }

      if (vTitleEl) vTitleEl.textContent = v.title;

      if (atc) {
        atc.disabled = !v.available;
        if (v.available) { atc.removeAttribute('aria-disabled'); } else { atc.setAttribute('aria-disabled', 'true'); }
        if (atcLabel && soldOutText) {
          atcLabel.textContent = v.available ? atc.getAttribute('data-add-label') : soldOutText;
        }
      }

      if (v.media_id) showMedia(v.media_id);

      pdp.querySelectorAll('[data-pdp-swatch]').forEach(function (b) {
        var on = String(b.getAttribute('data-variant-id')) === String(v.id);
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-checked', on ? 'true' : 'false');
      });

      /* Keep the address bar shareable, matching Shopify's own behaviour. */
      if (window.history && window.history.replaceState) {
        var u = new URL(window.location.href);
        u.searchParams.set('variant', v.id);
        window.history.replaceState({}, '', u.toString());
      }
    };

    pdp.querySelectorAll('[data-pdp-swatch]').forEach(function (b) {
      b.addEventListener('click', function () { selectVariant(b.getAttribute('data-variant-id')); });
    });

    var vSelect = pdp.querySelector('[data-pdp-select]');
    if (vSelect) {
      vSelect.addEventListener('change', function () { selectVariant(vSelect.value); });
    }

    /* -- Quantity stepper -- */
    var qtyInput = pdp.querySelector('[data-pdp-qty-input]');
    var bump = function (delta) {
      if (!qtyInput) return;
      var n = parseInt(qtyInput.value, 10);
      if (isNaN(n)) n = 1;
      qtyInput.value = Math.max(1, n + delta);
      qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
    };
    var qDown = pdp.querySelector('[data-pdp-qty-down]');
    var qUp = pdp.querySelector('[data-pdp-qty-up]');
    if (qDown) qDown.addEventListener('click', function () { bump(-1); });
    if (qUp) qUp.addEventListener('click', function () { bump(1); });
    if (qtyInput) {
      qtyInput.addEventListener('change', function () {
        var n = parseInt(qtyInput.value, 10);
        if (isNaN(n) || n < 1) qtyInput.value = 1;
      });
    }

    /* -- Accordions: <details> stays the source of truth, height is animated --
       Native <details> gives correct semantics and works with JS off; we only
       intercept the toggle to tween the panel height. */
    pdp.querySelectorAll('[data-pdp-acc]').forEach(function (acc) {
      var summary = acc.querySelector('summary');
      var panel = acc.querySelector('[data-pdp-acc-panel]');
      if (!summary || !panel) return;
      var animating = false;

      var settle = function () {
        panel.style.height = '';
        panel.style.overflow = '';
        animating = false;
      };

      var tween = function (from, to, closeAfter) {
        animating = true;
        panel.style.overflow = 'hidden';
        panel.style.height = from + 'px';
        if (reduce) {
          if (closeAfter) acc.open = false;
          settle();
          return;
        }
        var ease = getComputedStyle(document.documentElement).getPropertyValue('--ease').trim() || 'ease';
        requestAnimationFrame(function () {
          panel.style.transition = 'height .45s ' + ease;
          panel.style.height = to + 'px';
          /* Scoped to the panel's own height transition — a transition on any
             child (e.g. a link in the rich text) must not end this tween early. */
          var done = function (ev) {
            if (ev.target !== panel || ev.propertyName !== 'height') return;
            panel.removeEventListener('transitionend', done);
            panel.style.transition = '';
            if (closeAfter) acc.open = false;
            settle();
          };
          panel.addEventListener('transitionend', done);
        });
      };

      summary.addEventListener('click', function (e) {
        e.preventDefault();
        if (animating) return;
        if (acc.open) {
          tween(panel.scrollHeight, 0, true);
        } else {
          acc.open = true;
          tween(0, panel.scrollHeight, false);
        }
      });
    });
  }

  /* ---- Init orchestration ---- */
  function initAll(scope) {
    bindGlobal();
    initHeader(scope);
    observeReveals(scope);
    initHero(scope);
    initMagnetic(scope);
    initPcardHover(scope);
    initSlider(scope);
    initBA(scope);
    initVideo(scope);
    initTesti(scope);
    initNewsletter(scope);
    initMobileNav(scope);
    initCart(scope);
    initAnnouncement(scope);
    initMegaMenu(scope);
    initPdp(scope);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
  document.addEventListener('shopify:section:select', function (e) { initAll(e.target); });
})();
